import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import type {
  CreateOrgMcpServerInput,
  UpdateOrgMcpServerInput,
  OrgServiceAccountCredential,
} from '../types/mcp';

function useOrgId(): string {
  const { user } = useAuth();
  // Org-admin routes are gated by <RequireOrgAdmin>, which only renders once
  // the user is loaded — by the time any of these hooks fire, orgId is set.
  return user?.orgId ?? '';
}

function useOrgMcpServerApi() {
  const orgId = useOrgId();
  return useMemo(() => orgApi(orgId).mcpServers, [orgId]);
}

export function useOrgMcpServers() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  return useQuery({
    queryKey: ['org', orgId, 'mcp-servers'],
    queryFn: () => api.list(),
    enabled: !!orgId,
  });
}

export function useOrgMcpServer(serverId: string | undefined) {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  return useQuery({
    queryKey: ['org', orgId, 'mcp-servers', serverId],
    queryFn: () => api.get(serverId!),
    enabled: !!orgId && !!serverId,
  });
}

export function useCreateOrgMcpServer() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrgMcpServerInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'mcp-servers'] });
    },
  });
}

export function useUpdateOrgMcpServer() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      serverId,
      input,
    }: {
      serverId: string;
      input: UpdateOrgMcpServerInput;
    }) => api.update(serverId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'mcp-servers'] });
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'mcp-servers', variables.serverId],
      });
    },
  });
}

export function useDeleteOrgMcpServer() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) => api.delete(serverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'mcp-servers'] });
    },
  });
}

export function useSyncOrgMcpServer() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { serverId: string; returnUrl?: string }) =>
      api.sync(input.serverId, input.returnUrl),
    onSuccess: (_data, { serverId }) => {
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'mcp-servers', serverId],
      });
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'mcp-servers'] });
    },
  });
}

/**
 * Store (or replace) the org install's service-account credential. Backend
 * has PUT-only semantics in v1 — no delete endpoint, so the UI exposes only
 * "Add" / "Replace" affordances, never "Clear".
 */
export function usePutOrgMcpServiceAccountCredential() {
  const orgId = useOrgId();
  const api = useOrgMcpServerApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      serverId: string;
      credential: OrgServiceAccountCredential;
    }) => api.putServiceAccountCredential(input.serverId, input.credential),
    onSuccess: (_data, { serverId }) => {
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'mcp-servers', serverId],
      });
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'mcp-servers'] });
    },
  });
}
